import { PDFDocument } from "pdf-lib";

export async function mergePdfDocuments(inputs: readonly Uint8Array[]): Promise<Uint8Array> {
  if (inputs.length < 2) throw new Error("Merge requires at least two PDF files.");
  const output = await PDFDocument.create();
  for (const bytes of inputs) {
    const source = await PDFDocument.load(bytes, { updateMetadata: false });
    const pages = await output.copyPages(source, source.getPageIndices());
    for (const page of pages) output.addPage(page);
  }
  return output.save({ useObjectStreams: true, addDefaultPage: false });
}

export async function splitPdfDocument(
  input: Uint8Array,
  groups: readonly (readonly number[])[]
): Promise<Uint8Array[]> {
  if (groups.length === 0) throw new Error("Split requires at least one output group.");
  const source = await PDFDocument.load(input, { updateMetadata: false });
  const pageCount = source.getPageCount();
  const results: Uint8Array[] = [];

  for (const group of groups) {
    if (group.length === 0) throw new Error("Split output groups cannot be empty.");
    for (const page of group) {
      if (!Number.isInteger(page) || page < 1 || page > pageCount) {
        throw new Error(`Page ${page} is outside 1-${pageCount}.`);
      }
    }
    const output = await PDFDocument.create();
    const pages = await output.copyPages(source, group.map((page) => page - 1));
    for (const page of pages) output.addPage(page);
    results.push(await output.save({ useObjectStreams: true, addDefaultPage: false }));
  }
  return results;
}

export async function sha256Hex(input: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", input as BufferSource);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function getPdfPageCount(input: Uint8Array): Promise<number> {
  return (await PDFDocument.load(input, { updateMetadata: false })).getPageCount();
}

export interface PdfImageInput {
  mimeType: "image/png" | "image/jpeg" | string;
  bytes: Uint8Array;
}

export async function convertImagesToPdf(inputs: readonly PdfImageInput[]): Promise<Uint8Array> {
  if (inputs.length === 0) throw new Error("Image-to-PDF conversion requires at least one image.");
  const output = await PDFDocument.create();
  for (const input of inputs) {
    const image = input.mimeType === "image/png"
      ? await output.embedPng(input.bytes)
      : input.mimeType === "image/jpeg"
        ? await output.embedJpg(input.bytes)
        : null;
    if (!image) throw new Error("Image-to-PDF conversion supports PNG or JPEG input.");
    const page = output.addPage([image.width, image.height]);
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  }
  return output.save({ useObjectStreams: true, addDefaultPage: false });
}

export type PdfTextFormat = "text" | "markdown" | "html" | "json" | "xml" | "csv";

export interface SerializedPdfText {
  bytes: Uint8Array;
  mimeType: string;
  extension: "txt" | "md" | "html" | "json" | "xml" | "csv";
}

const encoder = new TextEncoder();

function escapeMarkup(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

function csvCell(value: string): string {
  const neutralized = /^[\t\r ]*[=+\-@]/.test(value) ? `'${value}` : value;
  return `"${neutralized.replaceAll('"', '""')}"`;
}

export function serializePdfText(pages: readonly string[], format: PdfTextFormat): SerializedPdfText {
  if (pages.length === 0) throw new Error("PDF text conversion requires at least one page.");
  if (format === "text") return { bytes: encoder.encode(`${pages.join("\n\f\n")}\n`), mimeType: "text/plain;charset=utf-8", extension: "txt" };
  if (format === "markdown") {
    const body = pages.map((text, index) => `## Page ${index + 1}\n\n${text}`).join("\n\n---\n\n");
    return { bytes: encoder.encode(`${body}\n`), mimeType: "text/markdown;charset=utf-8", extension: "md" };
  }
  if (format === "html") {
    const body = pages.map((text, index) => `<section data-page="${index + 1}"><h2>Page ${index + 1}</h2><pre>${escapeMarkup(text)}</pre></section>`).join("");
    return { bytes: encoder.encode(`<!doctype html><html><head><meta charset="utf-8"><title>Converted PDF</title></head><body>${body}</body></html>`), mimeType: "text/html;charset=utf-8", extension: "html" };
  }
  if (format === "json") {
    return { bytes: encoder.encode(JSON.stringify({ pages: pages.map((text, index) => ({ page: index + 1, text })) }, null, 2)), mimeType: "application/json", extension: "json" };
  }
  if (format === "xml") {
    const body = pages.map((text, index) => `<page number="${index + 1}">${escapeMarkup(text)}</page>`).join("");
    return { bytes: encoder.encode(`<?xml version="1.0" encoding="UTF-8"?><document>${body}</document>`), mimeType: "application/xml", extension: "xml" };
  }
  const rows = pages.map((text, index) => `${index + 1},${csvCell(text)}`).join("\r\n");
  return { bytes: encoder.encode(`page,text\r\n${rows}\r\n`), mimeType: "text/csv;charset=utf-8", extension: "csv" };
}

export async function extractPdfTextPages(input: Uint8Array): Promise<string[]> {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
  const document = await pdfjs.getDocument({ data: input.slice() }).promise;
  const pages: string[] = [];
  try {
    for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
      const page = await document.getPage(pageNumber);
      const content = await page.getTextContent();
      const text = content.items
        .filter((item): item is typeof item & { str: string } => "str" in item)
        .map((item) => item.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      pages.push(text);
      page.cleanup();
    }
  } finally {
    await document.cleanup();
  }
  return pages;
}
